import React from 'react'
import PropTypes from 'prop-types'
import DivButton from '../DivButton/DivButton.jsx'
import _ from 'lodash'

// This is a PureComponent rather than stateless functional so that callers
// are able to assign it a ref. It attempts to be like a native html element
// that can be used in 2 ways:
// Stateless where parent owns the state: in this case the value prop is provided
// with an onChange handler
// Stateful where the component owns the state: in this case the parent assigns a ref
// to the DropdownMenu and uses that to fetch its value (ex this.DropdownRef.value)
// additionally the reset() function may be accessed via the ref to set the component
// back to its defaultValue
class DropdownMenu extends React.PureComponent {
  htmlFor = _.uniqueId('dropDownMenu-')
  state = {
    value: this.props.defaultValue || this.props.value
  }
  reset = () => {
    this.value = this.props.defaultValue || this.props.value
    this.setState({ value: this.props.defaultValue || this.props.value })
  }
  componentDidMount() {
    this.reset()
  }
  componentDidUpdate(prevProps, prevSstate) {
    // this.value is what is accessible by a parent accessing this component
    // by ref (ex this.dropDownRef.value) which makes it behave like a native
    // html component. Here is where we sync that value with state.value
    if (this.value !== this.state.value) {
      this.value = this.state.value
    }
  }
  static getDerivedStateFromProps(nextProps, prevState) {
    const newState = {}
    if (prevState.value !== nextProps.value) {
      newState.value = nextProps.value
    }
    if (Object.keys(newState).length === 0) {
      return null
    } else {
      return newState
    }
  }
  render() {
    const { options } = this.props
    const index = this.props.alternateIndex || 'value'
    let value
    // find the appropriate value to display
    const selected = options.filter(option => {
      // double equals because we dont know what datatypes will be used
      return option[index] == this.state.value
    })[0]
    if (selected === undefined) {
      value = this.props.selectText || 'Select an option'
    } else {
      value = selected.name
    }
    const title = this.props.title || null
    const logEvent = this.props.logEvent || null;
    let buttonStyle = {}
    if (this.props.title && this.props.disabled) {
      buttonStyle = {
        borderRadius: '0px 5px 5px 0px',
        backgroundColor: '#eeeeee'
      }
    } else if (this.props.title) {
      buttonStyle = { borderRadius: '0px 5px 5px 0px' }
    }
    const menuStyle = this.props.maxMenuLength
      ? { maxHeight: this.props.maxMenuLength, overflowX: 'auto' }
      : { overflowX: 'hidden' }
    const hideDisabledMenu = this.props.disabled
      ? { display: 'none' }
      : menuStyle
    const menuAlignment =
      this.props.leftMenu === true ? 'dropdown-menu-right' : ''
    const isHtmlForNull =
      this.props.htmlFor == null ? this.htmlFor : this.props.htmlFor
    return (
      <div className={'input-group-component ' + (this.props.className || '')}>
        <div
          className="input-group"
          style={this.props.style}
          data-toggle={this.props['data-toggle']}
          data-title={this.props['data-title']}
          data-placement={this.props['data-placement'] || 'top'}
        >
          {title
            ? (
            <label
              style={{ margin: '0' }}
              className={
                'input-group-addon title ' + (this.props.titleClassName || '')
              }
              aria-label={title}
              htmlFor={isHtmlForNull}
            >
              {title}
            </label>
              )
            : null}
          <div className={this.props.dropUp === true ? 'dropup' : 'dropdown'}>
            <DivButton
              className={
                'btn btn-default dropdown-toggle ' +
                (this.props.valueClassName || '')
              }
              id="dropdown-divButton"
              aria-labelledby={logEvent !== null ? `${logEvent} dropdown menu` : title !== null ? `${title} dropdown menu` : `dropdown menu`}
              style={buttonStyle}
              type="button"
              data-toggle={this.props.disabled ? null : 'dropdown'}
              aria-haspopup="true"
              aria-expanded="true"
              data-cy="dropdown-button"
              disabled={this.props.disabled || false}
            >
              <span className="button-text">{value}</span>{' '}
              <span className="caret" />
            </DivButton>
            <ul
              id={isHtmlForNull}
              className={`dropdown-menu ${menuAlignment}`}
              style={hideDisabledMenu}
            >
              {options.map((option, idx) => {
                return (
                  <DropDownOption
                    key={idx}
                    handler={this.props.handler}
                    onChange={this.props.onChange}
                    value={option[index]}
                    {...option}
                  />
                )
              })}
            </ul>
          </div>
        </div>
      </div>
    )
  }
}

class DropDownOption extends React.PureComponent {
  handlerWrapper = () => {
    // this.value would be updated automatically in compWillUp
    // but do it here in case the parent tries to call value by
    // ref before that has a chance to run
    const value = this.props.value
    this.value = value
    if (this.props.handler) {
      this.props.handler(value)
    } else if (this.props.onChange) {
      this.props.onChange(value)
    }
    this.setState({ value })
  }
  render() {
    const { name, value } = this.props
    return (
      <li onClick={this.handlerWrapper}>
        <a
          className="button-text ellipsis-text"
          value={value}
          aria-labelledby={name}
          role="button"
          title={name}
        >
          {name}
        </a>
      </li>
    )
  }
}

DropdownMenu.propTypes = {
  // A callback function.
  onChange: PropTypes.func,
  handler: PropTypes.func, // (depricated)
  // An optional value corresponding to the value property of one of the objects
  // in the options array that should be preselected.
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  // A default value to be applied when the component mounts
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  // A required array of objects to display of type
  // {value:"string", name:"string"} where name is what is displayed in the list
  options: PropTypes.array.isRequired,
  // In the event the options array contains items with additional properties,
  // alternateIndex can provide the name of a property other than value to use
  // as the unique index for each object
  alternateIndex: PropTypes.string,
  // default text to display when no option is selected
  selectText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  // optional dropup menu
  dropUp: PropTypes.bool,
  // optional overflow menu to left
  leftMenu: PropTypes.bool,
  // should this control be disabled?
  disabled: PropTypes.bool,
  // css string indicating the max length of the menu div before it scrolls
  maxMenuLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // tooltip title
  'data-title': PropTypes.string,
  // optional class name added to the value portion of the input (e.g., for styling required or invalid entries)
  valueClassName: PropTypes.string,
  // If this component is used along with the SettingsTitle component then the prop will be needed in order to meet accessibility guidelines.
  htmlFor: PropTypes.string
}

export default DropdownMenu
